import { useState } from "react";
import { FileText, Save, Check, X } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { usePrompts, useUpdatePrompt } from "@/hooks/useAdminData";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

export default function Prompts() {
  const { data: prompts, isLoading } = usePrompts();
  const updatePrompt = useUpdatePrompt();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");

  const handleEdit = (id: string, template: string) => {
    setEditingId(id);
    setEditValue(template);
  };

  const handleSave = async (id: string) => {
    try {
      await updatePrompt.mutateAsync({ id, prompt_template: editValue });
      setEditingId(null);
      toast.success("Промпт сохранён");
    } catch (error) {
      toast.error("Ошибка при сохранении");
    }
  };

  const handleToggle = async (id: string, isActive: boolean) => {
    try {
      await updatePrompt.mutateAsync({ id, is_active: isActive });
      toast.success(isActive ? "Промпт активирован" : "Промпт деактивирован");
    } catch (error) {
      toast.error("Ошибка при обновлении");
    }
  };

  return (
    <AdminLayout title="Промпты" description="Системные промпты для AI генерации">
      <div className="space-y-4">
        {isLoading ? (
          [1, 2].map((i) => (
            <Skeleton key={i} className="h-48 w-full" />
          ))
        ) : (
          prompts?.map((prompt) => (
            <Card key={prompt.id} className="bg-card border-border">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-primary" />
                    {prompt.name}
                  </CardTitle>
                  <div className="flex items-center gap-3">
                    <Badge variant={prompt.type === 'claude' ? 'default' : 'secondary'}>
                      {prompt.type === 'claude' ? '🧠 Claude' : '🎵 Suno'}
                    </Badge>
                    {prompt.is_active ? (
                      <span className="text-xs text-primary flex items-center gap-1">
                        <Check className="w-3 h-3" /> Активен
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <X className="w-3 h-3" /> Неактивен
                      </span>
                    )}
                    <Switch
                      checked={prompt.is_active}
                      onCheckedChange={(checked) => handleToggle(prompt.id, checked)}
                    />
                  </div>
                </div>
                <CardDescription>
                  Переменные: {"{{event}}"}, {"{{genre}}"}, {"{{story}}"}, {"{{genre_style}}"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {editingId === prompt.id ? (
                  <div className="space-y-3">
                    <Textarea
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      rows={10}
                      className="font-mono text-sm"
                    />
                    <div className="flex gap-2">
                      <Button onClick={() => handleSave(prompt.id)} size="sm">
                        <Save className="w-4 h-4 mr-2" />
                        Сохранить
                      </Button>
                      <Button variant="outline" onClick={() => setEditingId(null)} size="sm">
                        Отмена
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div 
                    className="bg-muted/50 p-4 rounded-lg font-mono text-sm whitespace-pre-wrap cursor-pointer hover:bg-muted/70 transition-colors"
                    onClick={() => handleEdit(prompt.id, prompt.prompt_template)}
                  >
                    {prompt.prompt_template}
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </AdminLayout>
  );
}
